// Payroll Audit Logging System

import { supabase } from '@/integrations/supabase/client';
import { 
  PayrollPeriod, 
  PayrollAuditEntry, 
  PayrollStatus 
} from '@/types/payroll-periods';
import { format } from 'date-fns';

// Audit action types
export type PayrollAuditAction = 
  | 'calculate'
  | 'recalculate'
  | 'approve'
  | 'finalize'
  | 'pay'
  | 'lock'
  | 'unlock'
  | 'export'
  | 'view'
  | 'edit_employee_data'
  | 'delete_payroll'
  | 'bulk_operation'
  | 'template_apply'
  | 'status_change'
  | 'period_change'
  | 'security_action';

// Audit context interface
interface AuditContext {
  businessId: string;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
}

// Payroll audit service
export class PayrollAuditService {
  private static instance: PayrollAuditService;
  private context: AuditContext | null = null;

  private constructor() {}

  static getInstance(): PayrollAuditService {
    if (!PayrollAuditService.instance) {
      PayrollAuditService.instance = new PayrollAuditService();
    }
    return PayrollAuditService.instance;
  }

  // Set audit context for the session
  setContext(context: AuditContext) {
    this.context = context;
  }

  // Clear audit context
  clearContext() {
    this.context = null;
  }

  // Get current context
  private getContext(): AuditContext {
    if (!this.context) {
      throw new Error('Audit context not set. Call setContext() first.');
    }
    return this.context;
  }

  // Log a payroll action
  async logAction(params: {
    action: PayrollAuditAction;
    description?: string;
    period?: PayrollPeriod;
    payrollId?: string;
    previousStatus?: PayrollStatus;
    newStatus?: PayrollStatus;
    affectedEmployees?: string[];
    actionData?: Record<string, unknown>;
    severity?: 'low' | 'medium' | 'high' | 'critical';
  }): Promise<string | null> {
    try {
      const context = this.getContext();
      
      const auditEntry = {
        business_id: context.businessId,
        user_id: context.userId || null,
        action_type: params.action,
        action_description: params.description || this.generateDescription(params),
        payroll_id: params.payrollId || null,
        previous_status: params.previousStatus || null,
        new_status: params.newStatus || null,
        affected_employees: params.affectedEmployees || [],
        period_year: params.period?.year || null,
        period_month: params.period?.month || null,
        action_data: {
          ...params.actionData,
          severity: params.severity || 'medium',
          timestamp: new Date().toISOString(),
          context: {
            ipAddress: context.ipAddress,
            userAgent: context.userAgent,
            sessionId: context.sessionId
          }
        },
        ip_address: context.ipAddress || null,
        user_agent: context.userAgent || null
      };

      const { data, error } = await supabase
        .from('payroll_audit_log')
        .insert(auditEntry)
        .select('id')
        .single();

      if (error) {
        console.error('Failed to log payroll audit entry:', error);
        // In production, you might want to send this to an error monitoring service
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error('Error in audit logging:', error);
      return null;
    }
  }

  // Generate automatic description for actions
  private generateDescription(params: {
    action: PayrollAuditAction;
    period?: PayrollPeriod;
    affectedEmployees?: string[];
    actionData?: Record<string, unknown>;
  }): string {
    const { action, period, affectedEmployees, actionData } = params;
    const periodStr = period ? `for ${period.displayName}` : '';
    const employeeCount = affectedEmployees?.length || 0;
    
    switch (action) {
      case 'calculate':
        return `Calculated payroll ${periodStr} for ${employeeCount} employee(s)`;
      case 'recalculate':
        return `Recalculated payroll ${periodStr} for ${employeeCount} employee(s)`;
      case 'approve':
        return `Approved payroll ${periodStr}`;
      case 'finalize':
        return `Finalized payroll ${periodStr} - cannot be modified`;
      case 'pay':
        return `Processed payments ${periodStr} for ${employeeCount} employee(s)`;
      case 'lock':
        return `Locked payroll period ${periodStr}`;
      case 'unlock':
        return `Unlocked payroll period ${periodStr}`;
      case 'export': {
        const exportType = actionData?.exportType || 'data';
        return `Exported ${exportType} ${periodStr}`;
      }
      case 'view':
        return `Viewed payroll ${periodStr}`;
      case 'edit_employee_data':
        return `Modified employee payroll data ${periodStr}`;
      case 'delete_payroll':
        return `Deleted payroll records ${periodStr}`;
      case 'bulk_operation': {
        const operation = actionData?.operation || 'operation';
        return `Performed bulk ${operation} ${periodStr}`;
      }
      case 'template_apply': {
        const templateName = actionData?.templateName || 'template';
        return `Applied template "${templateName}" ${periodStr}`;
      }
      case 'status_change':
        return `Changed payroll status ${periodStr}`;
      case 'period_change':
        return `Changed active payroll period to ${periodStr}`;
      case 'security_action': {
        const securityAction = actionData?.securityAction || 'security action';
        return `Performed ${securityAction} ${periodStr}`;
      }
      default:
        return `Performed ${action} ${periodStr}`;
    }
  }

  // Log period navigation (for user behavior tracking)
  async logPeriodNavigation(fromPeriod: PayrollPeriod, toPeriod: PayrollPeriod) {
    return this.logAction({
      action: 'period_change',
      description: `Navigated from ${fromPeriod.displayName} to ${toPeriod.displayName}`,
      period: toPeriod,
      actionData: {
        fromPeriod: {
          year: fromPeriod.year,
          month: fromPeriod.month
        },
        toPeriod: {
          year: toPeriod.year,
          month: toPeriod.month
        }
      },
      severity: 'low'
    });
  }

  // Log payroll calculation
  async logPayrollCalculation(
    period: PayrollPeriod, 
    employeeIds: string[], 
    calculationResult: {
      totalGross: number;
      totalNet: number;
      calculationMethod: string;
      warnings?: string[];
    }
  ) {
    return this.logAction({
      action: 'calculate',
      period,
      affectedEmployees: employeeIds,
      actionData: {
        calculationResult,
        employeeCount: employeeIds.length
      },
      severity: 'high'
    });
  }

  // Log payroll status change
  async logStatusChange(
    period: PayrollPeriod,
    previousStatus: PayrollStatus,
    newStatus: PayrollStatus,
    employeeIds: string[] = [],
    reason?: string
  ) {
    return this.logAction({
      action: 'status_change',
      period,
      previousStatus,
      newStatus,
      affectedEmployees: employeeIds,
      actionData: {
        reason,
        statusTransition: `${previousStatus} → ${newStatus}`
      },
      severity: this.getStatusChangeSeverity(previousStatus, newStatus)
    });
  }

  // Log payment processing
  async logPaymentProcessing(
    period: PayrollPeriod,
    payments: Array<{
      employeeId: string;
      amount: number;
      paymentMethod: string;
    }>,
    totalAmount: number
  ) {
    return this.logAction({
      action: 'pay',
      period,
      affectedEmployees: payments.map(p => p.employeeId),
      actionData: {
        paymentCount: payments.length,
        totalAmount,
        payments: payments.map(p => ({
          employeeId: p.employeeId,
          amount: p.amount,
          paymentMethod: p.paymentMethod
        }))
      },
      severity: 'critical'
    });
  }

  // Log data export
  async logDataExport(
    period: PayrollPeriod,
    exportType: 'csv' | 'pdf' | 'payslips' | 'summary',
    employeeIds: string[] = []
  ) {
    return this.logAction({
      action: 'export',
      period,
      affectedEmployees: employeeIds,
      actionData: {
        exportType,
        employeeCount: employeeIds.length
      },
      severity: 'medium'
    });
  }

  // Log security-related actions
  async logSecurityAction(
    action: string,
    period?: PayrollPeriod,
    details?: Record<string, unknown>
  ) {
    return this.logAction({
      action: 'security_action',
      period,
      actionData: {
        securityAction: action,
        ...details
      },
      severity: 'high'
    });
  }

  // Get severity level for status changes
  private getStatusChangeSeverity(
    from: PayrollStatus, 
    to: PayrollStatus
  ): 'low' | 'medium' | 'high' | 'critical' {
    // Critical changes
    if (to === 'paid' || to === 'locked') return 'critical';
    if (from === 'paid' || from === 'locked') return 'critical';
    
    // High importance changes
    if (to === 'finalized' || from === 'finalized') return 'high';
    if (to === 'approved' || from === 'approved') return 'high';
    
    // Medium importance changes
    if (to === 'calculated' || from === 'calculated') return 'medium';
    
    // Low importance changes
    return 'low';
  }

  // Get audit logs for a period
  async getAuditLogs(params: {
    businessId: string;
    period?: PayrollPeriod;
    startDate?: Date;
    endDate?: Date;
    actions?: PayrollAuditAction[];
    limit?: number;
    offset?: number;
  }): Promise<PayrollAuditEntry[]> {
    try {
      let query = supabase
        .from('payroll_audit_log')
        .select(`
          id,
          business_id,
          payroll_id,
          user_id,
          action_type,
          action_description,
          previous_status,
          new_status,
          affected_employees,
          period_year,
          period_month,
          action_data,
          ip_address,
          user_agent,
          created_at,
          auth.users(email)
        `)
        .eq('business_id', params.businessId)
        .order('created_at', { ascending: false });

      if (params.period) {
        query = query
          .eq('period_year', params.period.year)
          .eq('period_month', params.period.month);
      }

      if (params.startDate) {
        query = query.gte('created_at', params.startDate.toISOString());
      }

      if (params.endDate) {
        query = query.lte('created_at', params.endDate.toISOString());
      }

      if (params.actions && params.actions.length > 0) {
        query = query.in('action_type', params.actions);
      }

      if (params.limit) {
        query = query.limit(params.limit);
      }

      if (params.offset) {
        query = query.range(params.offset, params.offset + (params.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to fetch audit logs:', error);
        return [];
      }

      return (data || []).map(entry => ({
        id: entry.id,
        businessId: entry.business_id,
        period: entry.period_year && entry.period_month ? {
          year: entry.period_year,
          month: entry.period_month,
          date: new Date(entry.period_year, entry.period_month - 1, 1),
          displayName: format(new Date(entry.period_year, entry.period_month - 1, 1), 'MMMM yyyy')
        } : undefined,
        action: entry.action_type,
        userId: entry.user_id,
        userName: entry.users?.email || 'Unknown User',
        timestamp: new Date(entry.created_at),
        details: entry.action_data || {},
        affectedEmployees: entry.affected_employees || [],
        previousState: entry.previous_status,
        newState: entry.new_status
      }));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  }

  // Get audit statistics
  async getAuditStatistics(params: {
    businessId: string;
    period?: PayrollPeriod;
    days?: number;
  }) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (params.days || 30));

      let query = supabase
        .from('payroll_audit_log')
        .select('action_type, created_at')
        .eq('business_id', params.businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (params.period) {
        query = query
          .eq('period_year', params.period.year)
          .eq('period_month', params.period.month);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to fetch audit statistics:', error);
        return null;
      }

      const stats = {
        totalActions: data?.length || 0,
        actionsByType: {} as Record<string, number>,
        actionsByDay: {} as Record<string, number>
      };

      data?.forEach(entry => {
        // Count by action type
        stats.actionsByType[entry.action_type] = (stats.actionsByType[entry.action_type] || 0) + 1;
        
        // Count by day
        const day = format(new Date(entry.created_at), 'yyyy-MM-dd');
        stats.actionsByDay[day] = (stats.actionsByDay[day] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching audit statistics:', error);
      return null;
    }
  }
}

// Export singleton instance
export const payrollAuditService = PayrollAuditService.getInstance();

// Export singleton instance as default
export { payrollAuditService as default };